import React, { useEffect, useRef, useState } from "react";
import { Typography, Chip, Box, Paper, Divider, Stack } from "@mui/material";
import { Work as WorkIcon } from "@mui/icons-material";

const experienceData = [
  {
    title: "Application Developer Intern",
    company: "Teachers' Retirement System of Louisiana",
    location: "Baton Rouge, LA",
    period: "February 2026 – Present",
    status: "current",
    highlights: [
      "Developed high-performance data access layers with C#/.NET, Dapper ORM, and MS SQL Stored Procedures, implementing Test-Driven Development via xUnit.NET.",
      "Managed the full development lifecycle through Azure DevOps pipelines and sprint planning.",
      "Engineered scalable solutions using Blazor WebAssembly and ASP.NET Core Web API to ensure modular, maintainable code.",
    ],
    tech: ["C#", ".NET", "Blazor", "ASP.NET Core", "Dapper", "MS SQL", "xUnit.NET", "Azure DevOps"],
  },
  {
    title: "Student Software Developer",
    company: "Southeastern Louisiana University",
    location: "Hammond, LA",
    period: "January 2025 – Present",
    status: "current",
    highlights: [
      "Revamped a legacy PHP/NGINX site (erm.selu.edu) by fixing deprecated code and encoding issues; enabled XML-to-HTML generation, allowing 600+ manuscripts to be annotated and boosting research collaboration.",
      "Improved site performance and visibility by 70% via SEO and modernized legacy systems with comprehensive technical documentation.",
      "Integrated historical maps and timelines using open-source Leaflet and TimelineJS for digital humanities research.",
    ],
    tech: ["PHP", "Nginx", "Docker", "Elastic Search", "Oxygen XML", "SEO", "Scrum", "Leaflet", "TimelineJS"],
  },
  {
    title: "Software Engineer Intern",
    company: "NorthShore STEM Coalition",
    location: "Hammond, LA",
    period: "January 2026 – February 2026",
    status: "past",
    highlights: [
      "Implemented data-driven admin workflows and optimized Azure-hosted services by fixing JWT authorization and right-sizing computing resources, reducing infrastructure costs by ~60%.",
      "Architected a scalable booking and inventory system with real-time availability checks and conflict prevention.",
    ],
    tech: ["Azure", "JWT", "REST API", "Admin Workflows"],
  },
  {
    title: "Consulting & Software Engineering Intern",
    company: "DXC Technology",
    location: "Ashburn, VA",
    period: "May–Aug 2024 & June–Aug 2025",
    status: "past",
    highlights: [
      "Developed a Manufacturing Execution System (MES) using Node.js and MS SQL; conducted client SWOT analyses for strategic business insight.",
      "Built and tested Azure Functions for system deployment and integrated Material UI to support admin-controlled badge tracking.",
      "Developed ServiceNow applications, implemented Agile-aligned UAT strategies, and created a Requirements Traceability Matrix to ensure project and stakeholder alignment.",
    ],
    tech: ["Node.js", "MS SQL", "Azure Functions", "ServiceNow", "Docker", "Jira", "Agile", "Solumina", "SDLC"],
  },
  {
    title: "Computer Science & Praxis Tutor",
    company: "Southeastern Louisiana University",
    location: "Hammond, LA",
    period: "March 2024 – December 2024",
    status: "past",
    highlights: [
      "Conducted specialized tutoring in core CS courses from CMPS 161 through Data Structures (CMPS 390) and Operating Systems (CMPS 431).",
      "Enhanced student proficiency in data structures and OOP using positive reinforcement; developed 50+ Praxis study materials aligned with curriculum standards.",
    ],
    tech: ["Java", "Python", "Data Structures", "OOP"],
  },
];

export const ExperienceSection = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Paper
      ref={ref}
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        mb: 4,
        borderRadius: 4,
        border: "1px solid var(--border-color)",
        background: "var(--card-bg)",
        boxShadow: "0 8px 32px rgba(102,126,234,0.08)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            flexShrink: 0,
            boxShadow: "0 4px 12px rgba(102,126,234,0.3)",
          }}
        >
          <WorkIcon sx={{ color: "white", fontSize: 21 }} />
        </Box>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: "var(--body_color)" }}
        >
          Professional Experience
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {/* Timeline */}
      <Box sx={{ position: "relative" }}>
        {/* Vertical line */}
        <Box
          sx={{
            position: "absolute",
            left: { xs: 10, md: 14 },
            top: 8,
            bottom: 8,
            width: "2px",
            background:
              "linear-gradient(180deg, #667eea 0%, #764ba2 60%, rgba(118,75,162,0.15) 100%)",
            borderRadius: 1,
          }}
        />

        {experienceData.map((exp, idx) => (
          <Box
            key={idx}
            sx={{
              position: "relative",
              pl: { xs: 4.5, md: 5.5 },
              pb: idx < experienceData.length - 1 ? 4 : 0,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(-16px)",
              transition: `opacity 0.5s ease-out ${idx * 0.1}s, transform 0.5s ease-out ${idx * 0.1}s`,
            }}
          >
            {/* Timeline dot */}
            <Box
              sx={{
                position: "absolute",
                left: { xs: 4, md: 8 },
                top: 6,
                width: 14,
                height: 14,
                borderRadius: "50%",
                background:
                  exp.status === "current"
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "var(--card-bg)",
                border: "2px solid",
                borderColor:
                  exp.status === "current" ? "#667eea" : "rgba(102,126,234,0.4)",
                boxShadow:
                  exp.status === "current"
                    ? "0 0 0 4px rgba(102,126,234,0.15)"
                    : "none",
                zIndex: 1,
              }}
            />

            {/* Card */}
            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                background: "var(--card-bg)",
                border: "1px solid var(--border-color)",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "3px",
                  background:
                    exp.status === "current"
                      ? "linear-gradient(180deg, #667eea 0%, #764ba2 100%)"
                      : "rgba(102,126,234,0.25)",
                  borderRadius: "3px 0 0 3px",
                },
                "&:hover": {
                  borderColor: "rgba(102,126,234,0.3)",
                  boxShadow: "0 6px 20px rgba(102,126,234,0.12)",
                  transform: "translateX(4px)",
                },
              }}
            >
              {/* Title row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="700"
                      sx={{ color: "var(--body_color)", lineHeight: 1.3 }}
                    >
                      {exp.title}
                    </Typography>
                    {exp.status === "current" && (
                      <Chip
                        label="Current"
                        size="small"
                        sx={{
                          bgcolor: "rgba(102,126,234,0.12)",
                          color: "#667eea",
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          height: 20,
                          border: "1px solid rgba(102,126,234,0.25)",
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      mt: 0.2,
                    }}
                  >
                    {exp.company}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Chip
                    label={exp.period}
                    size="small"
                    sx={{
                      bgcolor: "rgba(102,126,234,0.08)",
                      color: "var(--body_color)",
                      opacity: 0.8,
                      fontWeight: 500,
                      fontSize: "0.7rem",
                      border: "1px solid var(--border-color)",
                    }}
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ color: "var(--body_color)", opacity: 0.5, mt: 0.4, fontSize: "0.68rem" }}
                  >
                    {exp.location}
                  </Typography>
                </Box>
              </Box>

              {/* Highlights */}
              <Box component="ul" sx={{ mt: 1.2, pl: 2.2, mb: 1.5 }}>
                {exp.highlights.map((h, i) => (
                  <Typography
                    component="li"
                    key={i}
                    variant="body2"
                    sx={{
                      mb: 0.6,
                      color: "var(--body_color)",
                      opacity: 0.82,
                      lineHeight: 1.6,
                      fontSize: "0.875rem",
                    }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>

              {/* Tech chips */}
              <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.6 }}>
                {exp.tech.map((t, i) => (
                  <Chip
                    key={i}
                    label={t}
                    size="small"
                    sx={{
                      fontSize: "0.68rem",
                      height: 22,
                      bgcolor: "rgba(102,126,234,0.07)",
                      color: "var(--body_color)",
                      border: "1px solid rgba(102,126,234,0.18)",
                      fontWeight: 500,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "rgba(102,126,234,0.15)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};
